import * as THREE from 'three';
import { createGlowTexture } from '../utils/createGlowTexture.js';

/**
 * Generate a paw-print texture on a canvas. Solid white shapes:
 * large rounded palm pad + 4 oval toe pads, matching a real dog paw.
 */
function createPawTexture() {
  const size = 256;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');

  // Warm gold to match Sirius - additive blending will make it glow
  const color = '255, 210, 130';

  // Helper: draw a rotated oval with radial gradient (solid center, soft edge)
  function drawOval(cx, cy, rx, ry, angle) {
    ctx.save();
    ctx.translate(cx, cy);
    ctx.rotate(angle);
    const r = Math.max(rx, ry);
    const grad = ctx.createRadialGradient(0, 0, 0, 0, 0, r);
    grad.addColorStop(0, `rgba(${color}, 1)`);
    grad.addColorStop(0.6, `rgba(${color}, 0.6)`);
    grad.addColorStop(1, `rgba(${color}, 0)`);
    ctx.fillStyle = grad;
    ctx.scale(rx / r, ry / r);
    ctx.beginPath();
    ctx.arc(0, 0, r, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  }

  // Palm pad - gradient fill clipped to the bezier shape
  ctx.save();
  ctx.beginPath();
  // Wide rounded bottom, slight dip at top center (heart-like palm pad)
  ctx.moveTo(128, 240);
  ctx.bezierCurveTo(50, 242, 20, 190, 35, 148);
  ctx.bezierCurveTo(44, 125, 70, 118, 100, 122);
  ctx.quadraticCurveTo(128, 130, 156, 122);
  ctx.bezierCurveTo(186, 118, 212, 125, 221, 148);
  ctx.bezierCurveTo(236, 190, 206, 242, 128, 240);
  ctx.closePath();
  ctx.clip();
  const palmGrad = ctx.createRadialGradient(128, 178, 0, 128, 178, 65);
  palmGrad.addColorStop(0, `rgba(${color}, 1)`);
  palmGrad.addColorStop(0.55, `rgba(${color}, 0.5)`);
  palmGrad.addColorStop(1, `rgba(${color}, 0)`);
  ctx.fillStyle = palmGrad;
  ctx.fillRect(0, 60, size, size);
  ctx.restore();

  // Toe pads - 4 glowing ovals
  // Outer two: smaller, angled outward
  // Inner two: taller, closer together, nearly vertical
  drawOval(55, 94, 18, 26, -0.4);
  drawOval(100, 58, 20, 36, -0.1);
  drawOval(156, 58, 20, 36, 0.1);
  drawOval(201, 94, 18, 26, 0.4);

  const tex = new THREE.CanvasTexture(canvas);
  tex.needsUpdate = true;
  return tex;
}

/**
 * Sirius - the first interactive star.
 *
 * Adds a visible warm-gold glow sprite at Sirius's actual position from the
 * HYG catalog. The gold color marks it as "discoverable" so Megha can tell
 * it apart from the thousands of background stars. Subtle pulse animation
 * draws the eye. Hover scales it up. Click opens the card overlay.
 *
 * The same gold-glow pattern will be reused for every other interactive
 * star (Birth Star, Family Constellation, Pulsar, etc.).
 */

// Shared warm-gold glow texture, generated once
const GOLD_GLOW = createGlowTexture('255, 215, 120');

export class Sirius {
  constructor({ starField, onClick }) {
    this.id = 'sirius';
    this.discoveryId = 'sirius';
    this.category = 'hobbies';
    this.interactable = true;
    this.onClickCallback = onClick;
    this._t = 0;
    this._hovering = false;

    const sirius = starField.findByName('Sirius');
    if (!sirius) {
      console.warn('Sirius not found in HYG catalog');
      return;
    }
    this.starData = sirius;

    const position = starField.getStarPosition(sirius);

    // Visible glow sprite - warm gold, large enough to stand out among the
    // thousands of cool-toned catalog stars
    const material = new THREE.SpriteMaterial({
      map: GOLD_GLOW,
      color: 0xffd88c,
      transparent: true,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      depthTest: false, // render on top regardless
    });

    this.mesh = new THREE.Sprite(material);
    this.mesh.position.copy(position);
    this.baseScale = 32;
    this.mesh.scale.setScalar(this.baseScale);
    this.mesh.renderOrder = 10;
    this.mesh.userData.star = this;
    this.material = material;

    // Paw print - faint white glow behind Sirius, visible only on hover.
    // Stored as a separate sprite; Game.js will add it to the sky.
    const pawMaterial = new THREE.SpriteMaterial({
      map: createPawTexture(),
      transparent: true,
      opacity: 0,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      depthTest: false,
    });
    this.paw = new THREE.Sprite(pawMaterial);
    this.paw.position.copy(position);
    this.paw.scale.setScalar(55);
    this.paw.renderOrder = 9;
    this.pawMaterial = pawMaterial;
    this._pawOpacity = 0;
  }

  onHover() {
    document.body.classList.add('star-hover');
    this._hovering = true;
  }

  onUnhover() {
    document.body.classList.remove('star-hover');
    this._hovering = false;
  }

  onClick() {
    if (this.onClickCallback) this.onClickCallback(this);
  }

  /**
   * HTML for this star's card overlay.
   * Content from copy/sirius-jimmy.md, finalized voice.
   */
  cardHTML() {
    return `
      <h2>Sirius</h2>
      <div class="label">Constellation: Canis Major. The Big Dog.</div>

      <p>Astronomers have been calling this star the Dog Star for thousands of years. It is the brightest star in Earth's entire night sky. Brighter than any other point of light, anywhere, on any night. You can see it from any country, any continent, any latitude where the sky gets dark enough.</p>

      <p>The Big Dog has been the brightest thing in the human sky since before there were words for sky.</p>

      <p>So this is the only fair place to put him.</p>

      <hr>

      <h3>Jimmy</h3>
      <div class="label">December 2020</div>

      <p>Your favorite being on Earth. He arrived in the middle of a year that needed him, and he has been arriving every day since.</p>

      <hr>

      <p>Sirius is visible from Seattle. It is also visible from Kerala. Half the planet apart, people looking up at the same Dog Star.</p>

      <p>Tonight, no matter where any of you are, when you look up, the brightest thing in your sky will always be him.</p>

      <p>The astronomers who named it the Dog Star did the math thousands of years ago. They didn't know about Jimmy specifically.</p>

      <p>They got it right anyway.</p>
    `;
  }

  update(dt) {
    this._t += dt;
    // Subtle pulse: ±6% scale, slow rhythm. Hover overrides with 1.25x.
    const pulse = 1 + Math.sin(this._t * 1.4) * 0.06;
    const targetScale = this._hovering ? this.baseScale * 1.25 : this.baseScale * pulse;
    const cur = this.mesh.scale.x;
    const next = cur + (targetScale - cur) * 0.18;
    this.mesh.scale.setScalar(next);

    // Paw print fade: lerp toward target opacity
    const pawTarget = this._hovering ? 0.45 : 0;
    this._pawOpacity += (pawTarget - this._pawOpacity) * 0.1;
    this.pawMaterial.opacity = this._pawOpacity;
  }
}
